import { create } from "zustand";
import { persist } from "zustand/middleware";
import { SNAPSHOT_CALL_INTERVAL, SNAPSHOT_QUANTITY_DEFINTION_TIMEOUT, SNAPSHOT_STEP_PRICE_CHANGE_TIMEOUT } from "../../constants/constant-numbers";


export const useRequestStatusStore = create(
    persist(
        (set, get) => ({

            requests: {},
            batches: {},   // <-- add this

            startRequest: (
                key,
                quantityTitle = "",
                initialValue = null,
                expectedValue = null,
                metadata = {}
            ) => {

                const id = crypto.randomUUID();

                set((state) => ({
                    requests: {
                        ...state.requests,

                        [key]: [
                            ...(state.requests[key] || []),

                            {
                                id,

                                quantityTitle,

                                requestStatus: "loading",

                                // waiting for snapshot/general API result
                                apiResultStatus: "waiting",

                                startTime: Date.now(),
                                requestEndTime: null,
                                requestDuration: null,
                                apiResultStartTime: null,
                                apiResultEndTime: null,
                                apiResultDuration: null,

                                info: {
                                    initialValue,
                                    expectedValue,
                                    finalValue: null,
                                },

                                metadata,

                                error: null,
                            },
                        ],
                    },
                }));

                return id;
            },


            finishRequest: (
                key,
                requestId,
                status = "success",
                error = null
            ) => {

                set((state) => {

                    const history =
                        state.requests[key] || [];


                    return {
                        requests: {
                            ...state.requests,

                            [key]:
                                history.map((item) => {

                                    if (item.id !== requestId) {
                                        return item;
                                    }


                                    const now = Date.now();

                                    return {
                                        ...item,
                                        requestStatus: status,
                                        requestEndTime: now,
                                        requestDuration: now - item.startTime,
                                        apiResultStatus: status === "error" ? "failed"
                                            : item.metadata?.type === "commission-step" ? "success"
                                            : item.apiResultStatus,
                                        error,
                                    };

                                }),
                        },
                    };

                });
            },

    checkSnapshotPrices: (quantitiesById) => {


        set((state) => {

            const updatedRequests = { ...state.requests };

            Object.keys(updatedRequests).forEach(key => {

                updatedRequests[key] = updatedRequests[key].map(request => {

                    if (
                        request.requestStatus !== "success" ||
                        request.apiResultStatus !== "waiting"
                    ) {
                        return request;
                    }


                    const now = Date.now();

                    if (request.metadata?.type === "commission-adjustment") {
                        const q = quantitiesById[request.metadata.quantityId];
                        const expected = request.metadata.expectedCommissions;
                        if (!q || !expected) return request;

                        const matches = Object.entries(expected).every(([side, value]) =>
                            Number(q[`${side}Commission`]) === Number(value)
                        );
                        if (matches) {
                            return {
                                ...request,
                                apiResultStatus: "success",
                                apiResultEndTime: now,
                                apiResultDuration: now - request.startTime,
                            };
                        }
                        if (now - request.startTime > SNAPSHOT_CALL_INTERVAL) {
                            return {
                                ...request,
                                apiResultStatus: "failed",
                                apiResultEndTime: now,
                                apiResultDuration: now - request.startTime,
                                error: "Snapshot did not reflect the commission adjustment.",
                            };
                        }
                        return request;
                    }


                    // ====================================
                    // Batch request
                    // ====================================
                    if (request.metadata?.type === "batch") {

                        const updatedItems =
                            request.metadata.items.map(item => {

                                const q =
                                    quantitiesById[item.nodeId];


                                if (!q) {
                                    return {
                                        ...item,
                                        finalValue: null,
                                    };
                                }


                                const currentValue =
                                    item.priceType === "buy"
                                        ? q.prices.buy.price
                                        : q.prices.sell.price;


                                return {
                                    ...item,
                                    finalValue: currentValue,
                                };

                            });


                        const allSucceeded =
                            updatedItems.every(item =>
                                Number(item.finalValue) ===
                                Number(item.expectedValue)
                            );


                        // Snapshot confirmed
                        if (allSucceeded) {

                            return {
                                ...request,

                                metadata: {
                                    ...request.metadata,
                                    items: updatedItems,
                                },

                                apiResultStatus: "success",

                                apiResultEndTime: now,

                                apiResultDuration:
                                    now - request.startTime,
                            };
                        }


                        // Snapshot timeout
                        if (
                            now - request.startTime >
                            SNAPSHOT_CALL_INTERVAL
                        ) {

                            return {
                                ...request,

                                metadata: {
                                    ...request.metadata,
                                    items: updatedItems,
                                },

                                apiResultStatus: "failed",

                                apiResultEndTime: now,

                                apiResultDuration:
                                    now - request.startTime,

                                error:
                                    "Snapshot values did not match expected values",
                            };
                        }


                        // Still waiting
                        return {
                            ...request,

                            metadata: {
                                ...request.metadata,
                                items: updatedItems,
                            },
                        };

                    }//////////////////

                    if (request.metadata?.type === "quantity-activation") {

                        const q = quantitiesById[request.metadata.quantityId];

                        if (!q) {
                            return request;
                        }

                        const currentValue = q.isActive;
                        const success = currentValue === request.info.expectedValue;

                        const now = Date.now();
                        if(success){
                            return {
                                ...request,
                                apiResultStatus: "success",

                                info: {
                                    ...request.info,
                                    finalValue: currentValue,
                                },

                                apiResultEndTime: now,
                                apiResultDuration: now - request.startTime,
                            };
                        }
                        // Still waiting for the snapshot
                        if (now - request.startTime < SNAPSHOT_QUANTITY_DEFINTION_TIMEOUT) {
                            return request;
                        }

                        // Timed out
                        return {
                            ...request,

                            apiResultStatus: "failed",

                            info: {
                                ...request.info,
                                finalValue: currentValue,
                            },

                            apiResultEndTime: now,
                            apiResultDuration: now - request.startTime,

                            error: `Snapshot did not reflect the expected activation state within ${Number(SNAPSHOT_QUANTITY_DEFINTION_TIMEOUT/1000)} seconds.`,
                        };

                    }
                    if (request.metadata?.type === "price-activation") {

                        const q = quantitiesById[request.metadata.quantityId];

                        if (!q) {
                            return request;
                        }

                        const currentValue =
                            request.metadata.priceType === "buy"
                                ? q.active.buy.isActive
                                : q.active.sell.isActive;

                        const success = currentValue === request.info.expectedValue;

                        const now = Date.now();

                        if (success) {
                            return {
                                ...request,

                                apiResultStatus: "success",

                                info: {
                                    ...request.info,
                                    finalValue: currentValue,
                                },

                                apiResultEndTime: now,

                                apiResultDuration: now - request.startTime,
                            };
                        }

                        // Still waiting for snapshot
                        if (now - request.startTime < SNAPSHOT_CALL_INTERVAL) {
                            return request;
                        }

                        // Timed out
                        return {
                            ...request,

                            apiResultStatus: "failed",

                            info: {
                                ...request.info,
                                finalValue: currentValue,
                            },

                            apiResultEndTime: now,

                            apiResultDuration: now - request.startTime,

                            error: "Snapshot did not reflect the expected price activation state within 15 seconds.",
                        };
                    }
                    if (request.metadata?.type === "step-price") {

                        const q = quantitiesById[request.metadata.quantityId];

                        if (!q) {
                            return request;
                        }


                        const currentValue =
                            q.steps.buy.value;


                        const success =
                            Number(currentValue) ===
                            Number(request.info.expectedValue);


                        const now = Date.now();


                        if (success) {

                            return {
                                ...request,

                                apiResultStatus: "success",

                                info: {
                                    ...request.info,
                                    finalValue: currentValue,
                                },

                                apiResultEndTime: now,

                                apiResultDuration:
                                    now - request.startTime,
                            };
                        }


                        // wait for snapshot
                        if (now - request.startTime < SNAPSHOT_STEP_PRICE_CHANGE_TIMEOUT) {
                            return request;
                        }

                        // timeout
                        return {
                            ...request,

                            apiResultStatus: "failed",

                            info: {
                                ...request.info,
                                finalValue: currentValue,
                            },

                            apiResultEndTime: now,

                            apiResultDuration:
                                now - request.startTime,

                            error:
                                "Snapshot did not reflect the expected step price within timeout.",
                        };
                    }


                    // ====================================
                    // Single request
                    // ====================================

                    const {
                        quantityId,
                        priceType,
                    } = request.metadata;


                    const q =
                        quantitiesById[quantityId];


                    if (!q) {
                        return request;
                    }


                    const currentValue =
                        priceType === "buy"
                            ? q.prices.buy.price
                            : q.prices.sell.price;


                    const success =
                        Number(request.info.expectedValue) ===
                        Number(currentValue);



                    // Snapshot confirmed
                    if (success) {

                        return {
                            ...request,

                            apiResultStatus: "success",

                            info: {
                                ...request.info,
                                finalValue: currentValue,
                            },

                            apiResultEndTime: now,

                            apiResultDuration:
                                now - request.startTime,
                        };

                    }

                    // Snapshot timeout
                    if (
                        now - request.startTime >
                        SNAPSHOT_CALL_INTERVAL
                    ) {

                        return {
                            ...request,

                            apiResultStatus: "failed",

                            info: {
                                ...request.info,
                                finalValue: currentValue,
                            },

                            apiResultEndTime: now,

                            apiResultDuration:
                                now - request.startTime,

                            error:
                                "Snapshot value did not match expected value",
                        };

                    }


                    // Still waiting
                    return request;

                });

            });


            return {
                requests: updatedRequests,
            };

        });

    },

    updateRequestMetadata: (key, requestId, metadata) => {
        set(state => ({
            requests:{
                ...state.requests,
                [key]: (state.requests[key] || []).map(item =>
                    item.id === requestId
                    ? {
                        ...item,
                        metadata
                    }
                    : item
                )
            }
        }))
    },

    hasPendingCommissionAdjustment: code => Object.values(get().requests).some(history =>
        history.some(request =>
            request.metadata?.type === "commission-adjustment" &&
            request.metadata.code === code &&
            request.apiResultStatus === "waiting" &&
            ["loading", "success"].includes(request.requestStatus)
        )
    ),

    hasPendingCommissionStep: code => Object.values(get().requests).some(history =>
        history.some(request =>
            request.metadata?.type === "commission-step" &&
            request.metadata.code === code &&
            request.requestStatus === "loading"
        )
    ),

    hasPendingStepPrice: (nodeId) => {

        const state = get();

        return Object.values(state.requests).some(history =>
            history.some(request => {

                return (
                    request.requestStatus === "success" &&
                    request.apiResultStatus === "waiting" &&
                    request.metadata?.type === "step-price" &&
                    request.metadata.quantityId === nodeId
                );

            })
        );

    },

    hasPendingPriceUpdateForQuantity: (nodeId) => {

        const state = get();

        return Object.values(state.requests).some(history =>
            history.some(request => {

                if (
                    request.requestStatus !== "success" ||
                    request.apiResultStatus !== "waiting"
                ) {
                    return false;
                }

                // // single update
                // if (request.metadata?.quantityId === nodeId) {
                //     return true;
                // }

                // batch update
                if (request.metadata?.type === "batch") {
                    return request.metadata.items.some(
                        item => item.nodeId === nodeId
                    );
                }

                return false;
            })
        );
    },
    
    hasPendingPriceActivation: (nodeId, side) => {

        const state = get();

        return Object.values(state.requests).some(history =>
            history.some(request => {

                if (request.requestStatus !== "success") {
                    return false;
                }

                if (request.apiResultStatus !== "waiting") {
                    return false;
                }

                if (request.metadata?.type !== "price-activation") {
                    return false;
                }

                return (
                    request.metadata.quantityId === nodeId &&
                    request.metadata.priceType === side
                );

            })
        );
    },


    hasPendingQuantityActivation: (nodeId) => {

        const state = get();

        return Object.values(state.requests).some(history =>
            history.some(request => {

                if (request.requestStatus !== "success") {
                    return false;
                }

                if (request.apiResultStatus !== "waiting") {
                    return false;
                }

                if (request.metadata?.type !== "quantity-activation") {
                    return false;
                }

                return request.metadata.quantityId === nodeId;

            })
        );

    },    

    removeRequestItem: (
        key,
        requestId
    ) => {

        set((state) => ({

            requests: {
                ...state.requests,

                [key]:
                    (state.requests[key] || [])
                    .filter(
                        item =>
                            item.id !== requestId
                    ),
            },

        }));

    },


    clearRequests: () => {

            set({
                requests: {},
            });

        },
    }),

        {
            name: "request-status-v2",
            storage: {

                getItem: (name) => {

                    const value =
                        sessionStorage.getItem(name);

                    return value
                        ? JSON.parse(value)
                        : null;

                },

                setItem: (name, value) => {

                    sessionStorage.setItem(
                        name,
                        JSON.stringify(value)
                    );

                },

                removeItem: (name) => {
                    sessionStorage.removeItem(name);
                },

            },
        }
    )
);
